import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useSettings } from '../app/settingsStore.js';

export function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Names a shortcut might be bound to, independent of the keyboard language.
 * The character the layout produced comes first, so the printed letter still
 * works. The physical key comes next, so an Arabic, Russian, or Greek layout
 * hits the same place on the keyboard.
 */
export function shortcutNames(e) {
  const names = [];
  const add = (name) => {
    if (name && !names.includes(name)) names.push(name);
  };
  const key = e.key || '';
  if (key === ' ' || key === 'Enter' || key === 'Escape') add(key);
  else if (key.length === 1) add(key.toLowerCase());

  const code = e.code || '';
  if (code === 'Space') add(' ');
  else if (code === 'Enter' || code === 'NumpadEnter') add('Enter');
  else if (code === 'Escape') add('Escape');
  else if (/^Key[A-Z]$/.test(code)) add(code.slice(3).toLowerCase());
  else if (!e.shiftKey && /^Digit[0-9]$/.test(code)) add(code.slice(5));
  else if (/^Numpad[0-9]$/.test(code)) add(code.slice(6));
  else if (code === 'Slash') add(e.shiftKey ? '?' : '/');
  else if (!e.shiftKey && code === 'BracketLeft') add('[');
  return names;
}

/**
 * Bind keys -> handlers. Ignores key presses while typing or with Ctrl/Meta/Alt.
 * A handler returning `false` leaves the event to other listeners.
 */
export function useHotkeys(map, enabled = true) {
  const ref = useRef(map);
  useEffect(() => {
    ref.current = map;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.isComposing || e.key === 'Dead' || e.key === 'Process') return;
      if (isTypingTarget(e.target)) return;
      if (document.documentElement.classList.contains('sheet-open')) return;
      const handler = shortcutNames(e)
        .map((name) => ref.current[name])
        .find(Boolean);
      if (handler && handler(e) !== false) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

export const GLOBAL_SHORTCUTS = [
  { keys: 'G then D', label: 'Go to decks' },
  { keys: 'G then B', label: 'Go to browser' },
  { keys: 'A', label: 'Add a card' },
  { keys: '/', label: 'Search cards' },
  { keys: '[', label: 'Collapse or expand the side menu' },
  { keys: '?', label: 'Open the guide' },
];

export const STUDY_SHORTCUTS = [
  { keys: 'Space / Enter', label: 'Show answer, then Good' },
  { keys: '1 2 3 4', label: 'Forgot / Hard / Good / Easy' },
  { keys: 'E', label: 'Edit this card' },
  { keys: 'S', label: 'Pause this card' },
  { keys: 'B', label: 'Skip until tomorrow' },
  { keys: 'F', label: 'Flag this card' },
  { keys: 'Z', label: 'Undo last answer' },
  { keys: 'Esc', label: 'Leave session' },
];

export function useGlobalHotkeys(enabled = true) {
  const navigate = useNavigate();
  const pendingG = useRef(0);
  const afterG = () => Date.now() - pendingG.current < 1200;
  useHotkeys(
    {
      g: () => {
        pendingG.current = Date.now();
      },
      d: () => (afterG() ? navigate('/') : false),
      b: () => (afterG() ? navigate('/browse') : false),
      a: () => navigate('/add'),
      '/': () => navigate('/browse?focus=1'),
      '?': () => navigate('/guide'),
      '[': () => {
        const { sidebar, set } = useSettings.getState();
        set({ sidebar: sidebar === 'full' ? 'icons' : 'full' });
      },
    },
    enabled,
  );
}
